// src/components/install/InstallButton.tsx
//
// Per-(IDE, entry) install affordance. The deeplink-vs-copy decision is driven
// by the InstallTarget's `effectiveMethod` (derived from both the IDE's static
// kind and the entry's serverKind) — NOT the IDE kind alone — so a deep-link IDE
// installing a local-command server correctly falls back to copy.
//
// For `effectiveMethod === 'deeplink'` it renders a primary "Install in <label>"
// button that navigates the browser to the IDE's registered protocol handler.
// For `effectiveMethod === 'copy'` it renders a "Copy config" button that writes
// the Config_JSON to the clipboard and confirms within ~1 second. When the
// clipboard write is rejected, the Config_JSON is shown as selectable text with a
// manual-copy hint referencing the IDE's config file path.

import { useState } from 'react'
import { Download, Copy, Check } from 'lucide-react'
import { Button } from '../ui/Button'
import { buildInstallTarget, type IdeDescriptor } from '../../lib/ideInstall'
import type { CatalogEntry } from '../../lib/catalog'

interface InstallButtonProps {
  ide: IdeDescriptor
  entry: CatalogEntry
  baseUrl: string
  onCopied?: () => void
}

export function InstallButton({ ide, entry, baseUrl, onCopied }: Readonly<InstallButtonProps>) {
  const [copied, setCopied] = useState(false)
  const [clipboardFailed, setClipboardFailed] = useState(false)

  const target = buildInstallTarget(ide, entry, baseUrl)

  function handleInstall() {
    if (target.deepLinkUrl) {
      globalThis.location.href = target.deepLinkUrl
    }
  }

  function handleCopy() {
    navigator.clipboard.writeText(target.configJson).then(
      () => {
        setClipboardFailed(false)
        setCopied(true)
        onCopied?.()
        setTimeout(() => setCopied(false), 1000)
      },
      () => {
        // Clipboard blocked/rejected: fall back to selectable text + manual hint.
        setClipboardFailed(true)
      },
    )
  }

  if (target.effectiveMethod === 'deeplink') {
    return (
      <Button variant="primary" onClick={handleInstall}>
        <Download size={14} />
        Install in {ide.label}
      </Button>
    )
  }

  return (
    <div>
      <Button variant="primary" onClick={handleCopy}>
        {copied ? <Check size={14} /> : <Copy size={14} />}
        {copied ? 'Copied' : 'Copy config'}
      </Button>
      {clipboardFailed && (
        <div style={{ marginTop: 'var(--space-2)' }}>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>
            Couldn't access the clipboard. Select the config below and copy it manually
            into <code>{ide.configFilePath}</code>.
          </div>
          <textarea
            readOnly
            value={target.configJson}
            onFocus={(e) => e.currentTarget.select()}
            rows={Math.min(target.configJson.split('\n').length + 1, 18)}
            style={{
              width: '100%', fontFamily: 'var(--font-mono, monospace)', fontSize: 12,
              background: 'var(--surface-hover)', color: 'var(--text-primary)',
              border: '1px solid var(--border)', borderRadius: 'var(--radius-s)',
              padding: 'var(--space-2)', lineHeight: 1.6, resize: 'vertical',
            }}
          />
        </div>
      )}
    </div>
  )
}
